import { Box, MenuItem, TextField } from "@mui/material";
import { useCallback, useState, type ChangeEvent } from "react";
import {
  InvoiceTransmissionMethod,
  type TenediCustomerIdentityFieldsFragment,
} from "../../../generated/graphql";
import {
  CUSTOMER_FIELD_COPY,
  INVOICE_METHOD_LABELS,
} from "../tenedi-connection-copy";
import {
  customerFormFromEntity,
  customerFormSchema,
  type CustomerFormValues,
  formErrors,
} from "../tenedi-connection-forms";
import IdentityCardLayout, { identityCardStyles } from "./identity-card-layout";
import IdentityTextField from "./identity-text-field";

const TEXT_FIELDS = [
  "isaId",
  "ediApplicationId",
  "ediContactCode",
  "stediPartnerId",
  "standardCarrierAlphaCode",
] as const satisfies ReadonlyArray<keyof CustomerFormValues>;

type TextFieldName = (typeof TEXT_FIELDS)[number];

const INVOICE_METHODS = Object.values(InvoiceTransmissionMethod);

const INVOICE_METHOD_BY_VALUE = new Map(
  INVOICE_METHODS.map((method) => [String(method), method]),
);

type CustomerIdentityCardProps = {
  readonly customer: TenediCustomerIdentityFieldsFragment;
  readonly saveDisabled: boolean;
  /** Saves the card; a rejection's message shows on the card. */
  readonly onSave: (values: CustomerFormValues) => Promise<void>;
};

/**
 * The customer's EDI identity: EDI ID, GS ID, contact code, Stedi partner id,
 * SCAC and invoice method, saved in one call. The page remounts the card
 * after each save.
 */
const CustomerIdentityCard = ({
  customer,
  saveDisabled,
  onSave,
}: CustomerIdentityCardProps) => {
  const [values, setValues] = useState<CustomerFormValues>(() =>
    customerFormFromEntity(customer),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleFieldChange = useCallback(
    (name: TextFieldName, value: string) => {
      setValues((previous) => ({ ...previous, [name]: value }));
    },
    [],
  );

  const handleInvoiceMethodChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = INVOICE_METHOD_BY_VALUE.get(event.target.value);
      if (next !== undefined) {
        setValues((previous) => ({
          ...previous,
          defaultInvoiceTransmissionMethod: next,
        }));
      }
    },
    [],
  );

  const handleSave = useCallback(() => {
    const found = formErrors(customerFormSchema, values);
    setErrors(found);
    setSaveError(null);
    if (Object.keys(found).length > 0) {
      return;
    }
    setSaving(true);
    onSave(values)
      .catch((error: unknown) => {
        setSaveError(error instanceof Error ? error.message : String(error));
      })
      .finally(() => {
        setSaving(false);
      });
  }, [onSave, values]);

  return (
    <IdentityCardLayout
      formError={errors.form}
      saveDisabled={saveDisabled}
      saveError={saveError}
      saveLabel="Save customer IDs"
      saving={saving}
      subtitle={
        customer.isActive
          ? "The forwarder sends tenders from these IDs, and Tenedi routes this customer's messages by them."
          : "This customer is inactive in the TMS. Check that you picked the right one."
      }
      title={`Customer identity: ${customer.displayName}`}
      onSave={handleSave}
    >
      <Box sx={identityCardStyles.grid}>
        {TEXT_FIELDS.map((name) => (
          <IdentityTextField
            key={name}
            copy={CUSTOMER_FIELD_COPY[name]}
            disabled={saving}
            error={errors[name]}
            name={name}
            value={values[name]}
            onChange={handleFieldChange}
          />
        ))}
        <TextField
          select
          disabled={saving}
          helperText={
            CUSTOMER_FIELD_COPY.defaultInvoiceTransmissionMethod.helper
          }
          label={CUSTOMER_FIELD_COPY.defaultInvoiceTransmissionMethod.label}
          size="small"
          value={values.defaultInvoiceTransmissionMethod}
          onChange={handleInvoiceMethodChange}
        >
          {INVOICE_METHODS.map((method) => (
            <MenuItem key={method} value={method}>
              {INVOICE_METHOD_LABELS[method]}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </IdentityCardLayout>
  );
};

export default CustomerIdentityCard;
